import { VideoNotificationDto } from '../constants';
import {
  VIDEO_SYNC_VERSION,
  WATCHED_IDS_MAX,
  WATCHED_IDS_TTL_MS,
  buildSyncKey,
} from './constants';

/**
 * Ids this browser has confirmed, per identity.
 *
 * The server already filters confirmed videos out of `/active`, so this is not
 * the source of truth — it closes the three windows where the client would
 * otherwise re-show a video the user has just finished:
 *
 *   · a reload between the POST and the next `/active`,
 *   · a tab opened while the backend's read model still lists the item,
 *   · a tab that learned about the confirmation from another tab and then
 *     reloaded.
 *
 * It stays tiny because `reconcileWatchedFlags` deletes every id the server has
 * stopped returning, which is the server confirming it no longer needs to be
 * remembered.
 */

interface WatchedEntry {
  id: number;
  at: number;
}

interface WatchedRecord {
  v: number;
  ids: WatchedEntry[];
}

const keyFor = (scope: string) => buildSyncKey(scope, 'watched');

const readRecord = (scope: string): WatchedEntry[] => {
  try {
    const raw = window.localStorage.getItem(keyFor(scope));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as WatchedRecord;
    if (!parsed || parsed.v !== VIDEO_SYNC_VERSION || !Array.isArray(parsed.ids)) {
      return [];
    }
    const now = Date.now();
    return parsed.ids.filter((entry) => {
      const id = Number(entry?.id);
      const at = Number(entry?.at);
      if (!Number.isFinite(id) || !Number.isFinite(at)) return false;
      const age = now - at;
      // Clamped at both ends: a stamp from a clock that runs ahead is dropped
      // rather than kept alive forever.
      return age >= 0 && age <= WATCHED_IDS_TTL_MS;
    });
  } catch {
    // Storage unavailable or corrupted — behave as "nothing remembered", which
    // is exactly how this feature worked before the store existed.
    return [];
  }
};

const writeRecord = (scope: string, ids: WatchedEntry[]): void => {
  try {
    const record: WatchedRecord = {
      v: VIDEO_SYNC_VERSION,
      ids: ids.slice(-WATCHED_IDS_MAX),
    };
    window.localStorage.setItem(keyFor(scope), JSON.stringify(record));
  } catch {
    // no-op: the queue still works, it just loses the reload guarantee
  }
};

export const readWatchedIds = (scope: string): number[] =>
  readRecord(scope).map((entry) => entry.id);

export const addWatchedId = (scope: string, id: number): void => {
  const numericId = Number(id);
  if (!Number.isFinite(numericId)) return;
  const entries = readRecord(scope);
  if (entries.some((entry) => entry.id === numericId)) return;
  writeRecord(scope, [...entries, { id: numericId, at: Date.now() }]);
};

export const clearWatchedIds = (scope: string): void => {
  try {
    window.localStorage.removeItem(keyFor(scope));
  } catch {
    // nothing to do
  }
};

/**
 * Stamps `isWatched` on anything this browser has already confirmed, and prunes
 * ids the server has stopped returning.
 *
 * Runs on every successful fetch, so it is also what keeps the store from
 * growing: an id absent from the response has been accepted server-side and no
 * longer needs remembering.
 */
export const reconcileWatchedFlags = (
  scope: string,
  videos: VideoNotificationDto[]
): VideoNotificationDto[] => {
  const entries = readRecord(scope);
  if (!entries.length) return videos;

  const pendingIds = new Set(videos.map((video) => Number(video.id)));
  const stillPending = entries.filter((entry) => pendingIds.has(entry.id));
  if (stillPending.length !== entries.length) writeRecord(scope, stillPending);
  if (!stillPending.length) return videos;

  const watchedIds = new Set(stillPending.map((entry) => entry.id));
  return videos.map((video) =>
    watchedIds.has(Number(video.id)) ? { ...video, isWatched: true } : video
  );
};
